import { FormApi } from "final-form";
import _ from "lodash";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { EmteriaRoles } from "src/data/roles";
import i18next from "src/i18n/config";
import { withRequiredRole } from "src/ui/navigation/utils/withRequiredRole";
import { PrimaryButton, SecondaryButton } from "src/ui/shared/CustomButton";
import { TextField } from "src/ui/shared/form/TextField";
import {
    MinimalTable,
    MinimalTableCell,
    MinimalTableCellWithTooltip,
    MinimalTableContainer,
    MinimalTableHeader,
    MinimalTableHeaderCell,
} from "src/ui/shared/table/MinimalTableComponents";
import { Header } from "src/ui/shared/table/TableHeader";
import { showErrorToast } from "src/ui/shared/toasts/Toasts";
import { Tooltip } from "src/ui/shared/Tooltip";
import { filesize } from "src/ui/utils/filesize";

import { fetchAllFiles, FetchAllFilesPayload } from "@admin/api/file";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { Box, Card, CardContent, CardHeader, TableRow, Typography } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import TimeService from "@services/timeService/timeService";
import { useFullPageTableLayoutStyles } from "@shared/table/FullPageTableLayout.style";
import { TableBody } from "@shared/table/TableBody";
import CustomTablePagination from "@shared/table/TablePagination";
import { pageIndexOffset } from "@shared/table/Tanstack.utils";
import { useContainerOverflow } from "@shared/table/useContainerOverflow";
import {
    createColumnHelper,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";

import { useStyles } from "./AdminFilesPage.style";
import TableItem from "./TableItem";

type FormValues = { fileName: string; workspaceId: number };
type Status = "pending" | "fulfilled" | "rejected" | "uninitialized";
export type State = {
    data: API.File[];
    count: number;
    status: Status;
};
const defaultState: State = {
    data: [],
    count: 0,
    status: "uninitialized",
};

const columnHelper = createColumnHelper<API.File>();

const tableColumns = [
    columnHelper.display({
        id: "collapseButton",
        header: "",
        enableSorting: false,
        cell: () => null,
    }),
    columnHelper.accessor("filename", {
        id: "filename",
        header: i18next.t("common:label.name"),
        cell: (info) => <MinimalTableCellWithTooltip title={info.getValue()} style={{ maxWidth: "300px" }} />,
        enableSorting: true,
    }),
    columnHelper.accessor("workspaceId", {
        id: "workspaceId",
        header: () => (
            <Tooltip title={i18next.t("common:label.workspaceId")} placement="top">
                <span style={{ fontWeight: 600 }}>WID</span>
            </Tooltip>
        ),
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor("groupId", {
        id: "groupId",
        header: () => (
            <Tooltip title={i18next.t("common:label.groupId")} placement="top">
                <span style={{ fontWeight: 600 }}>GID</span>
            </Tooltip>
        ),
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue() ?? "-"}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor((row) => filesize(row.size), {
        id: "fileSize",
        header: i18next.t("common:label.size"),
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor("ownerId", {
        id: "ownerId",
        header: i18next.t("common:label.owner"),
        cell: (info) => (
            <MinimalTableCell width={320}>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: false,
    }),
    columnHelper.accessor(
        (row) =>
            row.status ? i18next.t("pages:adminFilesPage.status.uploaded") : i18next.t("pages:adminFilesPage.status.initialized"),
        {
            id: "sourceType",
            header: i18next.t("common:label.status"),
            cell: (info) => (
                <MinimalTableCell>
                    <Typography>{info.getValue()}</Typography>
                </MinimalTableCell>
            ),
        },
    ),
    columnHelper.accessor((row) => TimeService.getLocalDate(row.createdDate, "ISO", "date"), {
        id: "modifiedDate",
        header: i18next.t("pages:adminFilesPage.column.createdDate"),
        cell: (info) => (
            <MinimalTableCell>
                <Tooltip title={TimeService.getLocalDate(info.row.original.createdDate, "ISO", "dateTime")}>
                    <Typography>{info.getValue()}</Typography>
                </Tooltip>
            </MinimalTableCell>
        ),
        sortingFn: (rowA, rowB) => {
            const timeA = new Date(rowA.original.createdDate).getTime();
            const timeB = new Date(rowB.original.createdDate).getTime();
            return timeA - timeB;
        },
    }),
];

const AdminFilesPage = () => {
    const [state, setState] = React.useState<State>(defaultState);
    const [filterState, setFilterState] = React.useState<Partial<FormValues>>({});
    const [sorting, setSorting] = React.useState<SortingState>([{ id: "modifiedDate", desc: true }]);
    const { classes } = useStyles();
    const { classes: layoutClasses, cx } = useFullPageTableLayoutStyles();
    const { t } = useTranslation(["pages", "common", "modals"]);

    const table = useReactTable<API.File>({
        data: state.data,
        columns: tableColumns,
        manualPagination: true,
        manualSorting: true,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageIndex: 0,
                pageSize: 12,
            },
        },
    });

    const { pageIndex, pageSize } = table.getState().pagination;

    const { ref: tableContainerRef, contentOverflows } = useContainerOverflow(true, [pageIndex, pageSize, state.data.length]);

    const onSubmit = (values: FormValues) => {
        table.setPageIndex(0);
        setFilterState(values);
    };
    const onClear = (form: FormApi<FormValues, Partial<FormValues>>) => {
        form.reset();
        setFilterState({});
        table.setPageIndex(0);
    };

    const handleResetFilter = () => {
        setFilterState({});
        table.setPageIndex(0);
    };

    React.useLayoutEffect(() => {
        const currentSort = sorting[0] || { id: "modifiedDate", desc: false };

        setState((prev) => ({ ...prev, status: "pending" }));
        void fetchAllFiles({
            ...filterState,
            pageSize: pageSize,
            pageIndex: pageIndex + pageIndexOffset,
            sortCriteria: currentSort.id as FetchAllFilesPayload["sortCriteria"],
            sortDirection: currentSort.desc ? "desc" : "asc",
        })
            .then((data) => setState({ data: data.list, count: data.count, status: "fulfilled" }))
            .catch((error) => {
                const err = error as Error;
                showErrorToast(err.message);
                setState((prev) => ({ ...prev, status: "rejected" }));
            });
    }, [filterState, pageIndex, pageSize, sorting]);

    if (state.status === "uninitialized") {
        return <PageContainer isLoading />;
    }

    return (
        <PageContainer fitViewport>
            <Form<FormValues>
                onSubmit={onSubmit}
                render={({ handleSubmit, form, values }) => (
                    <form onSubmit={handleSubmit}>
                        <Card className={classes.card}>
                            <CardHeader
                                title={
                                    <Box className={classes.cardTitleBox}>
                                        <Typography variant="h6">{t("pages:adminFilesPage.filters")}</Typography>
                                        {!_.isEmpty(filterState) ? (
                                            <Typography variant="caption" color="error">
                                                * {t("pages:adminFilesPage.youHaveActiveFilters")}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                }
                            />

                            <CardContent className={classes.cardContent}>
                                <TextField name="fileName" label={t("pages:adminFilesPage.input.fileName")} fullWidth />
                                <TextField name="workspaceId" label={t("common:label.workspaceId")} fullWidth />
                                <PrimaryButton type="submit" disabled={_.isEmpty(values)}>
                                    {t("common:button.apply")}
                                </PrimaryButton>
                                <SecondaryButton disabled={_.isEmpty(filterState)} onClick={() => onClear(form)}>
                                    {t("common:button.clear")}
                                </SecondaryButton>
                            </CardContent>
                        </Card>
                    </form>
                )}
            />

            <Box className={layoutClasses.tableLayout}>
                <Box className={layoutClasses.tableWrapper}>
                    <MinimalTableContainer
                        ref={tableContainerRef}
                        className={cx(layoutClasses.tableContainer, !contentOverflows && layoutClasses.tableContainerNoScroll)}
                    >
                        <MinimalTable>
                            <MinimalTableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <Header key={header.id} {...header} />
                                        ))}
                                        <MinimalTableHeaderCell width={80}></MinimalTableHeaderCell>
                                    </TableRow>
                                ))}
                            </MinimalTableHeader>
                            <TableBody
                                table={table}
                                filter={{ isActive: !_.isEmpty(filterState), onResetFilter: handleResetFilter }}
                                loading={state.status === "pending"}
                                noDataMessage={t("pages:adminFilesPage.noDownloadsAvailableYet")}
                            >
                                {table.getRowModel().rows.map((row) => (
                                    <TableItem key={row.id} setState={setState} row={row} />
                                ))}
                            </TableBody>
                        </MinimalTable>
                    </MinimalTableContainer>
                </Box>
                <CustomTablePagination
                    component="div"
                    className={layoutClasses.pagination}
                    rowsPerPageOptions={[12, 24, 48]}
                    count={state.count}
                    rowsPerPage={pageSize}
                    page={pageIndex}
                    onPageChange={(_event, page) => table.setPageIndex(page)}
                    onRowsPerPageChange={(e) => {
                        const size = e.target.value ? Number(e.target.value) : 12;
                        table.setPagination({ pageIndex: 0, pageSize: size });
                    }}
                />
            </Box>
        </PageContainer>
    );
};
export default withRequiredRole(
    withMetaDescription(AdminFilesPage, {
        title: i18next.t("pages:adminFilesPage.metaTitle"),
        description: i18next.t("pages:adminFilesPage.metaDescription"),
    }),
    [EmteriaRoles.EmteriaAdminRole],
);
